import { EventNames, globalEventBus } from '@littlebird/event-bus';
import {
  composeDraftMessageContent,
  isChatStatusBusy,
  type SteerChatPayload,
  type SteerChatStatus,
  SteerRefusedError,
  useSDK,
  useStores,
} from '@littlebird/sdk';
import { comparer, reaction } from 'mobx';
import { useCallback, useEffect } from 'react';
import { type ExternalToast, toast } from 'sonner';

import { chatStore, type QueuedChatMessage } from '@/features/chat/chat.store';
import {
  canJoinRunningTurn,
  getSendNowAction,
} from '@/features/chat/queued-messages';
import { logError } from '@/lib/errorLogging';
import { trackEvent } from '@/utils/analytics';
import { assertNever } from '@/utils/types';

/**
 * Longer than the backend's `PROCESSING_LEASE_DURATION` (30 seconds), the lease on a running
 * response, which lapses when the response's process dies
 */
const STEERED_MESSAGE_RECHECK_MS = 35_000;

/** The server's answer about a steered message once the response ended, or `unknown` when it could not be checked */
type SteeredMessageCheck = SteerChatStatus | 'unknown';

type UseMessageQueueOptions = {
  /** The chat whose queue this sends from; nothing runs without one */
  threadId: string | null;
  isBusy: boolean;
  canQueueMessages: boolean;
  isSteeringOn: boolean;
  /** Pauses automatic sending, for example while the account is out of credits */
  isAutoSendPaused?: boolean;
  /** Starts a normal send of a queued message, true once it started. A send that fails after starting must hold the queue */
  startSend: (threadId: string, message: QueuedChatMessage) => Promise<boolean>;
  /** Stops the running response; false when the server did not confirm the stop */
  stopResponse: (threadId: string) => Promise<boolean>;
  /** Queues the composer's draft, for the send-now hotkey while nothing is queued; without it the caller queues first */
  queueDraft?: () => Promise<QueuedChatMessage | undefined>;
  /** The app context sent with a steer request for a message that has none of its own */
  getSteerContext?: (threadId: string) => string | undefined;
  toastOptions?: ExternalToast;
};

/**
 * Sends a chat's queued messages: the first one when the response ends, or now through its
 * button (Interrupt, Send, or Steer, which hands it to the running response). Every composer
 * that queues messages uses it, so they all follow the same rules. A message whose steer request
 * got no answer may already be in the chat, so it is never sent normally before the server
 * confirms it does not have it
 */
export const useMessageQueue = ({
  threadId,
  isBusy,
  canQueueMessages,
  isSteeringOn,
  isAutoSendPaused = false,
  startSend,
  stopResponse,
  queueDraft,
  getSteerContext,
  toastOptions,
}: UseMessageQueueOptions) => {
  const sdk = useSDK();
  const {
    drafts: draftsStore,
    threads: threadsStore,
    messages: messagesStore,
  } = useStores();
  const queueHoldReason = threadId
    ? chatStore.queueHoldByThread.get(threadId)
    : undefined;
  const isQueueHeld = queueHoldReason !== undefined;
  const queuedSendInFlightId = threadId
    ? chatStore.queuedSendInFlightByThread.get(threadId)
    : undefined;
  const nextQueuedMessage = threadId
    ? chatStore.queuedMessagesByThread.get(threadId)?.[0]
    : undefined;
  const sendNowAction = canQueueMessages
    ? getSendNowAction({
        isSteeringOn,
        isBusy,
        holdReason: queueHoldReason,
        nextQueuedMessage,
      })
    : undefined;

  /** Removes the draft that holds a queued message's content */
  const removeQueuedDraft = useCallback(
    (message: QueuedChatMessage | undefined) => {
      const queuedDraft = message && draftsStore.getDraft(message.draftId);
      if (queuedDraft) draftsStore.removeDraft(queuedDraft);
    },
    [draftsStore]
  );

  useEffect(() => {
    if (!threadId) return;
    // Drops queued and steered messages once the chat has them. A queued one gets there when
    // the answer to its steer request was lost. The message being sent is handled by its own send.
    // One that reached the chat unread by the response gets a toast saying so.
    return reaction(
      () => {
        const inFlightId = chatStore.queuedSendInFlightByThread.get(threadId);
        const inChat = (messages: QueuedChatMessage[] = []) =>
          // Reads `data`, since `messagesStore.get` is an action and would not rerun the reaction
          messages.filter(({ id }) => messagesStore.data.has(id));
        return {
          queued: inChat(chatStore.queuedMessagesByThread.get(threadId))
            .filter(({ id }) => id !== inFlightId)
            .map(({ id, steerOutcomeUnknown }) => ({
              id,
              steerOutcomeUnknown: Boolean(steerOutcomeUnknown),
            })),
          steeredIds: inChat(
            chatStore.steeredMessagesByThread.get(threadId)
          ).map(({ id }) => id),
        };
      },
      ({ queued, steeredIds }) => {
        // Without `joinedTurn` the message reached the chat after the response ended
        const tellIfUnanswered = (id: string) => {
          if (messagesStore.data.get(id)?.joinedTurn) return;
          toast.info("Your message wasn't added to the response", {
            ...toastOptions,
            description: "It's in the chat without an answer.",
          });
        };
        queued.forEach(({ id, steerOutcomeUnknown }) => {
          removeQueuedDraft(chatStore.removeQueuedMessage(threadId, id));
          if (steerOutcomeUnknown) tellIfUnanswered(id);
        });
        steeredIds.forEach((id) => {
          removeQueuedDraft(chatStore.removeSteeredMessage(threadId, id));
          tellIfUnanswered(id);
        });
      },
      { equals: comparer.structural, fireImmediately: true }
    );
  }, [messagesStore, removeQueuedDraft, threadId, toastOptions]);

  useEffect(() => {
    if (!threadId) return;
    // A failed send shows as an error before its promise settles, and the queue would send the
    // next message in that gap, so the queue is held as soon as the failure is reported
    const subscription = globalEventBus.on(
      EventNames.CHAT_SEND_FAILED,
      ({ data }) => {
        if (
          data.threadId !== threadId ||
          data.reason === 'chat_busy' ||
          !chatStore.queuedMessagesByThread.has(threadId)
        ) {
          return;
        }
        chatStore.holdQueue(threadId, 'send_failed');
      }
    );
    return () => subscription.unsubscribe();
  }, [threadId]);

  /** Holds the queue after a steered message could not be checked, as after a failed send */
  const holdQueueAfterFailedCheck = useCallback(
    (holdThreadId: string) => {
      chatStore.holdQueue(holdThreadId, 'send_failed');
      toast.error("Couldn't check whether your message steered the response", {
        ...toastOptions,
        description:
          'Your queued messages are waiting. Press Send on the first one to try again.',
      });
    },
    [toastOptions]
  );

  /**
   * The steer request for a queued message, built from its draft as a normal send builds it, with
   * the app context captured when it was queued. Keep its `uploaded`-only file
   * filter in lockstep with `getDraftSendabilityError` in the SDK's `draft.ts`
   */
  const buildSteerPayload = useCallback(
    (message: QueuedChatMessage): SteerChatPayload => {
      const draft = draftsStore.getDraft(message.draftId);
      return {
        userMessageId: message.id,
        question: draft
          ? composeDraftMessageContent(draft.content, draft.selectedTexts)
          : message.content,
        context: message.context,
        fileIds: draft?.files
          .filter((file) => file.uploadState === 'uploaded')
          .map((file) => file.id),
      };
    },
    [draftsStore]
  );

  /** `buildSteerPayload`, plus the current app context for a message that captured none when it was queued */
  const buildSteerPayloadWithCurrentContext = useCallback(
    (steerThreadId: string, message: QueuedChatMessage): SteerChatPayload => {
      const payload = buildSteerPayload(message);
      return {
        ...payload,
        context: payload.context ?? getSteerContext?.(steerThreadId),
      };
    },
    [buildSteerPayload, getSteerContext]
  );

  /**
   * Checks with the server, once the response ended, whether it still has a steered message the
   * chat lacks. The request is a steer request, so a response that holds the chat stores a message
   * the server no longer has. It carries no current app context, since that belongs to a new send
   */
  const checkSteeredMessage = useCallback(
    async (
      checkThreadId: string,
      message: QueuedChatMessage
    ): Promise<SteeredMessageCheck> => {
      try {
        return await threadsStore.steerChat(
          checkThreadId,
          buildSteerPayload(message)
        );
      } catch (error) {
        logError(error, 'Failed to check a steered message', {
          context: 'Thread',
        });
        return 'unknown';
      }
    },
    [buildSteerPayload, threadsStore]
  );

  // A steer answered after the response ended adds a message while a check runs, so the
  // check starts again when the set changes, not only when the first message arrives
  const steeredMessageIds = threadId
    ? (chatStore.steeredMessagesByThread.get(threadId) ?? [])
        .map(({ id }) => id)
        .join(',')
    : '';
  const hasSteeredMessages = steeredMessageIds !== '';
  useEffect(() => {
    if (!threadId || isBusy || !hasSteeredMessages) return;
    // A response writes each steered message to the chat before it ends, whether the message
    // joined it or not. A steered message the chat still lacks after a sync is checked with the
    // server, and the answer decides what happens to it:
    // - accepted: a response still holds the chat, or the one that took the message died and its
    //   lease has not lapsed yet. It is checked again once the lease can have lapsed, and the
    //   server writes it when checked then
    // - delivered: the chat has it on the server, often just written there, so the chat syncs again,
    //   and once more when it still lacks it, since a sync that started before the server wrote it
    //   can miss it. One the chat still lacks then is taken to be on a branch the chat no longer
    //   shows, as after an edit of an earlier message, so it is dropped, not sent again
    // - not_accepted: the server no longer has it, so it goes back to the queue for a normal send
    // - unknown: the check failed, so it goes back to a held queue to be sent with Send
    // `steerQueuedMessage` is not reused, since it marks a message steered again, which would start
    // this check anew
    let isStale = false;
    let recheckTimer: ReturnType<typeof setTimeout> | undefined;
    const logCheckFailure = (error: unknown) =>
      logError(error, 'Failed to check steered messages', {
        context: 'Thread',
      });
    const resolveSteeredMessagesAfterResponse = async () => {
      // The server is checked even when the sync fails
      await sdk.syncNow().catch((error: unknown) =>
        logError(error, 'Failed to sync before checking steered messages', {
          context: 'Thread',
        })
      );
      if (isStale) return;
      const missingMessages = (
        chatStore.steeredMessagesByThread.get(threadId) ?? []
      ).filter(({ id }) => !messagesStore.data.has(id));
      const checks: { id: string; check: SteeredMessageCheck }[] = [];
      for (const message of missingMessages) {
        checks.push({
          id: message.id,
          check: await checkSteeredMessage(threadId, message),
        });
      }
      // A new response may still read the messages, so only the latest check acts
      if (isStale) return;
      const isAny = (wanted: SteeredMessageCheck) =>
        checks.some(({ check }) => check === wanted);
      chatStore.returnSteeredMessagesToQueue(
        threadId,
        checks.flatMap(({ id, check }) =>
          check === 'not_accepted' || check === 'unknown'
            ? [{ id, steerOutcomeUnknown: check === 'unknown' }]
            : []
        )
      );
      if (isAny('unknown')) holdQueueAfterFailedCheck(threadId);
      if (isAny('accepted')) {
        recheckTimer = setTimeout(() => {
          resolveSteeredMessagesAfterResponse().catch(logCheckFailure);
        }, STEERED_MESSAGE_RECHECK_MS);
      }
      if (isAny('delivered')) {
        const deliveredButMissing = () =>
          checks.filter(
            ({ id, check }) =>
              check === 'delivered' && !messagesStore.data.has(id)
          );
        await sdk.syncNow();
        if (!isStale && deliveredButMissing().length > 0) await sdk.syncNow();
        if (isStale) return;
        deliveredButMissing().forEach(({ id }) =>
          removeQueuedDraft(chatStore.removeSteeredMessage(threadId, id))
        );
      }
    };
    resolveSteeredMessagesAfterResponse().catch(logCheckFailure);
    return () => {
      isStale = true;
      clearTimeout(recheckTimer);
    };
  }, [
    checkSteeredMessage,
    hasSteeredMessages,
    holdQueueAfterFailedCheck,
    isBusy,
    messagesStore,
    removeQueuedDraft,
    sdk,
    steeredMessageIds,
    threadId,
  ]);

  /**
   * Hands a queued message to the steer endpoint. A repeat with the same id is safe: the server
   * answers with what it already did with that message. True when the server has the message,
   * which then shows as steering the response until the chat has it; false when it refused it.
   * When the outcome is unknown this marks the message and throws, since only the server knows
   * whether the response took it in
   */
  const steerQueuedMessage = useCallback(
    async (
      steerThreadId: string,
      message: QueuedChatMessage
    ): Promise<boolean> => {
      let status: SteerChatStatus;
      try {
        status = await threadsStore.steerChat(
          steerThreadId,
          buildSteerPayloadWithCurrentContext(steerThreadId, message)
        );
      } catch (error) {
        // A refusal proves only that this request stored nothing; after a lost answer the server may have it
        const refusalProvesNotStored =
          error instanceof SteerRefusedError && !message.steerOutcomeUnknown;
        if (!refusalProvesNotStored) {
          chatStore.setSteerOutcomeUnknown(steerThreadId, message.id, true);
          throw error;
        }
        status = 'not_accepted';
      }
      // No default, so a new status fails to compile until it is handled here
      switch (status) {
        case 'accepted':
        case 'delivered':
          // The draft stays until the chat has the message, which may still need sending
          chatStore.markQueuedMessageSteered(steerThreadId, message.id);
          return true;
        case 'not_accepted':
          chatStore.setSteerOutcomeUnknown(steerThreadId, message.id, false);
          return false;
      }
    },
    [buildSteerPayloadWithCurrentContext, threadsStore]
  );

  /**
   * Sends a queued message as a normal message. True when the send started; a failed send or a
   * failed check of an earlier steer request holds the queue. A message whose steer request got
   * no answer is first checked again, since a normal send of a message the server has would
   * show it twice
   */
  const sendQueuedMessage = useCallback(
    async (
      sendThreadId: string,
      message: QueuedChatMessage
    ): Promise<boolean> => {
      if (message.steerOutcomeUnknown) {
        try {
          if (await steerQueuedMessage(sendThreadId, message)) return false;
        } catch (error) {
          logError(
            error,
            'Failed to check a queued message before sending it',
            {
              context: 'Thread',
            }
          );
          holdQueueAfterFailedCheck(sendThreadId);
          return false;
        }
      }
      if (!draftsStore.getDraft(message.draftId)) {
        chatStore.removeQueuedMessage(sendThreadId, message.id);
        return false;
      }
      const didStartSend = await startSend(sendThreadId, message);
      if (didStartSend) {
        chatStore.removeQueuedMessage(sendThreadId, message.id);
      } else {
        chatStore.holdQueue(sendThreadId, 'send_failed');
      }
      return didStartSend;
    },
    [draftsStore, holdQueueAfterFailedCheck, startSend, steerQueuedMessage]
  );

  /** Sends the first queued message now, stopping the response first on Interrupt */
  const sendFirstQueuedMessageNow = useCallback(
    async (sendThreadId: string, needsStop: boolean) => {
      const queuedMessage =
        chatStore.queuedMessagesByThread.get(sendThreadId)?.[0];
      // Only Interrupt queues the draft when nothing is queued; Send acts on a held queue
      if (!needsStop && !queuedMessage) return;
      const message = queuedMessage ?? (await queueDraft?.());
      if (!message || !chatStore.beginQueuedSend(sendThreadId, message.id)) {
        return;
      }
      try {
        if (needsStop && !(await stopResponse(sendThreadId))) {
          chatStore.holdQueue(
            sendThreadId,
            'stop_unacknowledged',
            threadsStore.getById(sendThreadId)?.lastMessageId
          );
          toast.error("Couldn't stop the response", {
            ...toastOptions,
            description:
              'Your message is still queued. Use Interrupt to retry.',
          });
          return;
        }
        chatStore.releaseQueue(sendThreadId);
        if (await sendQueuedMessage(sendThreadId, message)) {
          // `steered` is the older analytics name for Interrupt or Send from the queue, not the Steer button
          trackEvent('CHAT_STREAM_INTERRUPTED', { reason: 'steered' });
        }
      } finally {
        chatStore.endQueuedSend(sendThreadId, message.id);
      }
    },
    [queueDraft, sendQueuedMessage, stopResponse, threadsStore, toastOptions]
  );

  /**
   * Hands the first queued message to the running response without stopping it. When it is not
   * steered, the message stays queued for the response's end:
   * - A failed request: the "may not have steered" error toast shows.
   * - `not_accepted`: `steerChat` first checks the chat with the server, when the server's reason
   *   says the reply may have ended. If the check ends the chat, no toast shows, and the
   *   auto-send effect below sends the message; otherwise the "Couldn't steer" toast shows.
   * - A `SteerRefusedError` that proves the server stored nothing (`refusalProvesNotStored` in
   *   `steerQueuedMessage`): as `not_accepted`, with no check, since a 4xx says nothing about
   *   whether the response ended.
   */
  const steerFirstQueuedMessageNow = useCallback(
    async (steerThreadId: string) => {
      const message =
        chatStore.queuedMessagesByThread.get(steerThreadId)?.[0] ??
        (await queueDraft?.());
      // A message that cannot steer stays queued, and its disabled Steer button says why
      if (!message || !canJoinRunningTurn(message)) return;
      if (!chatStore.beginQueuedSend(steerThreadId, message.id)) return;
      try {
        const wasSteered = await steerQueuedMessage(steerThreadId, message);
        // Read from the store, since the steer's check with the server can end the chat
        const isStillQueuedBehindResponse =
          !wasSteered &&
          isChatStatusBusy(threadsStore.getById(steerThreadId)?.status);
        if (isStillQueuedBehindResponse) {
          toast.info("Couldn't steer the response", {
            ...toastOptions,
            description:
              'Your message stays queued and sends when the response ends.',
          });
        }
      } catch (error) {
        logError(error, 'Failed to steer the running turn', {
          context: 'Thread',
        });
        toast.error('Your message may not have steered the response', {
          ...toastOptions,
          description:
            'It stays queued. When the response ends, Littlebird checks and sends it if needed.',
        });
      } finally {
        chatStore.endQueuedSend(steerThreadId, message.id);
      }
    },
    [queueDraft, steerQueuedMessage, threadsStore, toastOptions]
  );

  /** Does what the first queued message's button shows */
  const sendNow = useCallback(async () => {
    if (!threadId || chatStore.queuedSendInFlightByThread.has(threadId)) {
      return;
    }
    switch (sendNowAction) {
      case 'steer':
        await steerFirstQueuedMessageNow(threadId);
        return;
      case 'interrupt':
      case 'send':
        await sendFirstQueuedMessageNow(
          threadId,
          sendNowAction === 'interrupt'
        );
        return;
      case 'steer_unavailable':
      case undefined:
        return;
      default:
        assertNever(sendNowAction);
    }
  }, [
    sendFirstQueuedMessageNow,
    sendNowAction,
    steerFirstQueuedMessageNow,
    threadId,
  ]);

  const removeQueuedMessage = useCallback(
    (message: QueuedChatMessage) => {
      if (
        !threadId ||
        chatStore.queuedSendInFlightByThread.get(threadId) === message.id
      ) {
        return;
      }
      removeQueuedDraft(chatStore.removeQueuedMessage(threadId, message.id));
    },
    [removeQueuedDraft, threadId]
  );

  const currentTurnMessageId = threadId
    ? threadsStore.getById(threadId)?.lastMessageId
    : undefined;
  useEffect(() => {
    // A turn that started after the hold proves the stopped one is over, so the hold is stale.
    // The unacknowledged turn's own late events also read busy, so compare the turn, not the status
    if (!threadId || !isBusy) return;
    if (
      queueHoldReason === 'stop_unacknowledged' &&
      chatStore.queueHoldTurnByThread.get(threadId) === currentTurnMessageId
    ) {
      return;
    }
    chatStore.releaseQueue(threadId);
  }, [currentTurnMessageId, isBusy, queueHoldReason, threadId]);

  useEffect(() => {
    // With queueing off the queue stays hidden but intact: a kill-switch rollback
    // must not drop unsent input, and the drafts only live for the session anyway.
    // A queued message waits for the steered ones before it, so the chat keeps their order
    if (
      !threadId ||
      !canQueueMessages ||
      queuedSendInFlightId !== undefined ||
      isBusy ||
      hasSteeredMessages ||
      isQueueHeld ||
      !nextQueuedMessage ||
      isAutoSendPaused
    ) {
      return;
    }
    if (!chatStore.beginQueuedSend(threadId, nextQueuedMessage.id)) return;
    void sendQueuedMessage(threadId, nextQueuedMessage).finally(() => {
      chatStore.endQueuedSend(threadId, nextQueuedMessage.id);
    });
  }, [
    canQueueMessages,
    hasSteeredMessages,
    isAutoSendPaused,
    isBusy,
    isQueueHeld,
    nextQueuedMessage,
    queuedSendInFlightId,
    sendQueuedMessage,
    threadId,
  ]);

  return {
    sendNowAction,
    canSendNow:
      sendNowAction !== undefined && sendNowAction !== 'steer_unavailable',
    hasQueuedMessage: canQueueMessages && nextQueuedMessage !== undefined,
    isSendingNow: queuedSendInFlightId !== undefined,
    sendNow,
    removeQueuedMessage,
  };
};
